import React, { useEffect, useMemo, useState } from 'react';
import { opsApi, type OpsSettings } from '../../../api/dbOps';
import { runnerApi, errorText, type StatusGrid } from '../../../api/migrationRunner';
import { Alert, Btn, ErrorBox, Panel, Pill, Spinner, when } from '../runner/ui';
import { input, sel, td, th, usePoll } from './shared';

/**
 * Settings › Run window and "Schedule a batch" (approved mockup "Database
 * redesign", Settings). A scheduled batch is started by the scheduler at its
 * time through the same checks as Apply now; with the run window on it must
 * fall inside the window.
 */

/** Pending pairs from the status grid: migration ids and targets with not_run cells. */
function pendingFrom(grid: StatusGrid | null) {
  if (!grid) return { migrationIds: [] as number[], targets: [] as string[] };
  const ids = new Set<number>();
  const targets = new Set<string>();
  for (const m of grid.migrations) {
    if (m.archived) continue;
    for (const t of grid.targets) {
      if (t.scope !== m.scope || t.exists === false) continue;
      if (grid.cells[m.scriptId]?.[t.targetDb]?.state === 'not_run') { ids.add(m.scriptId); targets.add(t.targetDb); }
    }
  }
  return { migrationIds: [...ids], targets: [...targets] };
}

/** "YYYY-MM-DDTHH:MM" for <input type=datetime-local> in the browser's zone. */
function localInput(d: Date) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function tonight11() {
  const d = new Date();
  d.setHours(23, 0, 0, 0);
  if (d.getTime() < Date.now()) d.setDate(d.getDate() + 1);
  return d;
}

const STATUS_TONE = { scheduled: 'info', started: 'ok', cancelled: 'mut', failed: 'bad' } as const;

const SchedulePanel: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const list = usePoll(() => opsApi.schedules(), 30000);
  const settingsQ = usePoll(() => opsApi.settings(), null);
  const grid = usePoll(() => runnerApi.status(), null);
  const [win, setWin] = useState<OpsSettings['run_window'] | null>(null);
  const [at, setAt] = useState(localInput(tonight11()));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  useEffect(() => { if (settingsQ.data) setWin(settingsQ.data.settings.run_window); }, [settingsQ.data]);
  const pending = useMemo(() => pendingFrom(grid.data), [grid.data]);
  const tz = settingsQ.data?.timezone || list.data?.timezone || 'America/New_York';

  const saveWindow = async (next: OpsSettings['run_window']) => {
    if (!settingsQ.data) return;
    setWin(next);
    try {
      await opsApi.saveSettings({ ...settingsQ.data.settings, run_window: next });
      void settingsQ.reload();
      void list.reload();
    } catch (err) {
      setMsg({ tone: 'bad', text: errorText(err) });
    }
  };

  const schedule = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const runAt = new Date(at);
      await opsApi.schedule({ runAt: runAt.toISOString(), migrationIds: pending.migrationIds, targets: pending.targets, label: `Scheduled for ${when(runAt.toISOString())}` });
      setMsg({ tone: 'ok', text: `Scheduled for ${when(runAt.toISOString())}. It runs with the usual checks at that time.` });
      void list.reload();
    } catch (err) {
      const d = (err as { response?: { data?: { next_window?: string } } })?.response?.data;
      setMsg({ tone: 'bad', text: `${errorText(err)}${d?.next_window ? ` Next window opens ${when(d.next_window)}.` : ''}` });
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (id: string) => {
    try { await opsApi.cancelSchedule(id); void list.reload(); } catch (err) { setMsg({ tone: 'bad', text: errorText(err) }); }
  };

  const hours = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);
  const upcoming = (list.data?.schedules || []).filter((s) => s.status === 'scheduled');
  const past = (list.data?.schedules || []).filter((s) => s.status !== 'scheduled').slice(0, 10);

  return (
    <div className="space-y-4">
      {list.data && !list.data.schema_ready && <Alert tone="warn" title="Scheduled batches need the registry migration migrations-2026-10-11-db-ops" />}
      <Panel title="Run window">
        <div className="flex flex-wrap items-center gap-3 text-[13.5px]">
          <span className="min-w-[220px] flex-1">Scheduled batches only start inside it.<small className="block text-[12px] text-slate-500">Times are {tz}. A batch that comes due outside it waits until it opens.</small></span>
          {win ? (
            <>
              <select className={sel} disabled={!isOwner} value={win.enabled ? 'window' : 'any'} onChange={(e) => saveWindow({ ...win, enabled: e.target.value === 'window' })}>
                <option value="any">Any time</option><option value="window">Only between</option>
              </select>
              {win.enabled && (
                <>
                  <select className={sel} disabled={!isOwner} value={win.start} onChange={(e) => saveWindow({ ...win, start: e.target.value })}>{hours.map((h) => <option key={h}>{h}</option>)}</select>
                  <span>and</span>
                  <select className={sel} disabled={!isOwner} value={win.end} onChange={(e) => saveWindow({ ...win, end: e.target.value })}>{hours.map((h) => <option key={h}>{h}</option>)}</select>
                </>
              )}
            </>
          ) : <Spinner />}
        </div>
      </Panel>

      <Panel title="Schedule a batch">
        <div className="flex flex-wrap items-center gap-3 text-[13.5px]">
          {grid.loading ? <Spinner /> : (
            <span>Apply <b>{pending.migrationIds.length} pending</b> to <b>{pending.targets.length} {pending.targets.length === 1 ? 'database' : 'databases'}</b></span>
          )}
          <input type="datetime-local" className={`${input} w-auto`} value={at} onChange={(e) => setAt(e.target.value)} disabled={!isOwner} />
          {isOwner && <Btn disabled={busy || !pending.migrationIds.length} onClick={schedule}>{busy && <Spinner />}Schedule</Btn>}
          {list.data?.run_window.enabled && list.data.next_window && <span className="text-[12px] text-slate-500">Next window opens {when(list.data.next_window)}</span>}
        </div>
        {grid.error ? <ErrorBox>{errorText(grid.error)}</ErrorBox> : null}
        {msg && <div className="mt-3"><Alert tone={msg.tone} title={msg.text} /></div>}
      </Panel>

      {(upcoming.length > 0 || past.length > 0) && (
        <Panel title="Scheduled batches" bodyClass="p-0">
          <table className="w-full">
            <thead className="bg-slate-50"><tr><th className={th}>Runs at</th><th className={th}>What</th><th className={th}>By</th><th className={th}>Status</th><th className={th} /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {[...upcoming, ...past].map((s) => (
                <tr key={s.id}>
                  <td className={`${td} whitespace-nowrap`}>{when(s.run_at.includes('T') ? s.run_at : `${s.run_at.replace(' ', 'T')}Z`)}</td>
                  <td className={td}>{s.migration_ids.length} migration{s.migration_ids.length === 1 ? '' : 's'} · {s.targets.length} database{s.targets.length === 1 ? '' : 's'}</td>
                  <td className={td}>{s.created_by}</td>
                  <td className={td}>
                    <Pill tone={STATUS_TONE[s.status]}>{s.status}</Pill>
                    {s.batch_id && <div className="mt-1 text-[11.5px] text-slate-500">batch {s.batch_id.slice(0, 8)}</div>}
                    {s.error_message && <div className="mt-1 max-w-xs text-[11.5px] text-red-700">{s.error_message}</div>}
                  </td>
                  <td className={`${td} text-right`}>{isOwner && s.status === 'scheduled' && <Btn kind="ghost" small onClick={() => cancel(s.id)}>Cancel</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
};

export default SchedulePanel;
